import { useState } from 'react'
import { ChevronDown, ChevronRight, Lock } from 'lucide-react'
import {
  handbookSections,
  type HandbookBlock,
  type HandbookSection,
} from '../data/teamHandbookContent'

/* Password aligned with product deck / deck-final style gates (dizee2026). */
const HANDBOOK_PASSWORD = 'dizee2026'

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string; bannerBg: string }> = {
  sky: {
    border: 'border-sky-500/20',
    bg: 'bg-sky-500/[0.04]',
    text: 'text-sky-400',
    pill: 'bg-sky-500/15 text-sky-300',
    bannerBg: 'bg-sky-500/[0.06] border-sky-500/15',
  },
  violet: {
    border: 'border-violet-500/20',
    bg: 'bg-violet-500/[0.04]',
    text: 'text-violet-400',
    pill: 'bg-violet-500/15 text-violet-300',
    bannerBg: 'bg-violet-500/[0.06] border-violet-500/15',
  },
  emerald: {
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/[0.04]',
    text: 'text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-300',
    bannerBg: 'bg-emerald-500/[0.06] border-emerald-500/15',
  },
  amber: {
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/[0.04]',
    text: 'text-amber-400',
    pill: 'bg-amber-500/15 text-amber-300',
    bannerBg: 'bg-amber-500/[0.06] border-amber-500/15',
  },
  rose: {
    border: 'border-rose-500/20',
    bg: 'bg-rose-500/[0.04]',
    text: 'text-rose-400',
    pill: 'bg-rose-500/15 text-rose-300',
    bannerBg: 'bg-rose-500/[0.06] border-rose-500/15',
  },
  cyan: {
    border: 'border-cyan-500/20',
    bg: 'bg-cyan-500/[0.04]',
    text: 'text-cyan-400',
    pill: 'bg-cyan-500/15 text-cyan-300',
    bannerBg: 'bg-cyan-500/[0.06] border-cyan-500/15',
  },
  indigo: {
    border: 'border-indigo-500/20',
    bg: 'bg-indigo-500/[0.04]',
    text: 'text-indigo-400',
    pill: 'bg-indigo-500/15 text-indigo-300',
    bannerBg: 'bg-indigo-500/[0.06] border-indigo-500/15',
  },
  zinc: {
    border: 'border-zinc-500/20',
    bg: 'bg-zinc-500/[0.04]',
    text: 'text-zinc-400',
    pill: 'bg-zinc-500/15 text-zinc-300',
    bannerBg: 'bg-zinc-500/[0.06] border-zinc-500/15',
  },
}

function BlockContent({ block }: { block: HandbookBlock }) {
  return (
    <div className="space-y-3">
      {block.heading && (
        <h4 className="text-[12px] font-semibold text-zinc-300 uppercase tracking-wider">{block.heading}</h4>
      )}
      {block.paragraphs?.map((p, i) => (
        <p key={i} className="text-[13px] text-zinc-400 leading-relaxed">
          {p}
        </p>
      ))}
      {block.bullets && block.bullets.length > 0 && (
        <ul className="space-y-2 pl-1">
          {block.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-2.5 text-[13px] text-zinc-400 leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 bg-zinc-600" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {block.numbered && block.numbered.length > 0 && (
        <ol className="list-decimal list-inside space-y-2 text-[13px] text-zinc-400 leading-relaxed marker:text-zinc-500">
          {block.numbered.map((item, i) => (
            <li key={i} className="pl-1">
              {item}
            </li>
          ))}
        </ol>
      )}
      {block.code && block.code.length > 0 && (
        <div className="rounded-lg border border-white/[0.06] bg-black/30 px-4 py-3 font-mono text-[12px] text-zinc-400 leading-relaxed whitespace-pre-wrap">
          {block.code.join('\n')}
        </div>
      )}
      {block.table && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-x-auto">
          <table className="w-full text-[12px] min-w-[320px]">
            <thead>
              <tr className="border-b border-white/[0.06]">
                {block.table.headers.map((h) => (
                  <th
                    key={h}
                    className="text-left px-4 py-3 text-zinc-500 font-medium uppercase tracking-wider whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {block.table.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => (
                    <td key={ci} className="px-4 py-2.5 text-zinc-300 align-top">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SectionBody({ section }: { section: HandbookSection }) {
  const c = colorMap[section.color] || colorMap.sky
  return (
    <div className="px-6 pb-6 space-y-5">
      <div className={`rounded-lg border p-4 ${c.bannerBg}`}>
        <p className="text-[12px] text-zinc-400 leading-relaxed">{section.lead}</p>
      </div>
      <div className="space-y-6">
        {section.blocks.map((block, i) => (
          <BlockContent key={i} block={block} />
        ))}
      </div>
    </div>
  )
}

export default function TeamHandbookPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === HANDBOOK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => setOpenSections(new Set(handbookSections.map((s) => s.id)))
  const collapseAll = () => setOpenSections(new Set())

  const totalTopics = handbookSections.length
  const apiRouteRows = handbookSections.find((s) => s.id === 'backend')?.blocks.find((b) => b.table)?.table?.rows
    .length ?? 0

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8 bg-zinc-950">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">
                Team Handbook
              </span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view the handbook.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                autoComplete="current-password"
                className="deck-final-input w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-base text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="deck-final-input w-full rounded-lg bg-white px-6 py-3 text-base font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            >
              View handbook
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">Confidential — For intended recipients only.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] rotate-[-25deg]">
          <span className="text-[180px] font-bold tracking-widest text-white whitespace-nowrap select-none">
            CONFIDENTIAL
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight shrink-0">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full truncate">
              Team Handbook
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider hidden sm:inline">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-10">
        <div className="space-y-3">
          <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">Team handbook</h1>
          <p className="text-[14px] text-zinc-400 leading-relaxed max-w-3xl">
            Plain-English map of how Dizee is built — frontend, backend, database, AI, email, and integrations.
            Same layout as Master Plan / March plan for easy scanning.
          </p>
          <p className="text-[11px] text-zinc-600">Last updated: March 2026</p>
        </div>

        <nav className="flex flex-wrap gap-2">
          {handbookSections.map((s) => {
            const c = colorMap[s.color] || colorMap.sky
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  if (!openSections.has(s.id)) toggleSection(s.id)
                  document.getElementById(`section-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-all hover:scale-[1.03] ${c.border} ${c.bg} ${c.text}`}
              >
                {s.number}. {s.title}
              </button>
            )
          })}
        </nav>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Topics', value: totalTopics, color: 'text-white' },
            { label: 'Main codebases', value: 2, color: 'text-sky-400' },
            { label: 'API route groups', value: apiRouteRows, color: 'text-emerald-400' },
            { label: 'Doc type', value: 'Internal', color: 'text-violet-400' },
          ].map((card) => (
            <div key={card.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
              <div className="text-[11px] text-zinc-500 uppercase tracking-wider mb-2">{card.label}</div>
              <div className={`text-2xl font-semibold ${card.color}`}>{card.value}</div>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={expandAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Expand all
          </button>
          <span className="text-zinc-700">|</span>
          <button
            type="button"
            onClick={collapseAll}
            className="text-[11px] text-zinc-500 hover:text-zinc-300 uppercase tracking-wider transition-colors"
          >
            Collapse all
          </button>
        </div>

        <div className="space-y-4">
          {handbookSections.map((section) => {
            const isOpen = openSections.has(section.id)
            const c = colorMap[section.color] || colorMap.sky
            const Icon = section.icon

            return (
              <div
                key={section.id}
                id={`section-${section.id}`}
                className={`rounded-xl border ${c.border} ${c.bg} overflow-hidden transition-all scroll-mt-24`}
              >
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-4 px-6 py-5 text-left group"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.pill}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-[11px] text-zinc-600 font-medium">#{section.number}</span>
                      <h2 className="text-[15px] font-semibold text-white">{section.title}</h2>
                    </div>
                    <p className="text-[12px] text-zinc-500 mt-1">{section.subtitle}</p>
                  </div>
                  <div className="flex-shrink-0 text-zinc-600 group-hover:text-zinc-400 transition-colors">
                    {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </div>
                </button>
                {isOpen && <SectionBody section={section} />}
              </div>
            )
          })}
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/[0.06] mt-16">
        <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <span className="text-[11px] text-zinc-600">Confidential — Internal use only</span>
          <span className="text-[11px] text-zinc-700">Canonical copy: src/data/teamHandbookContent.ts + /team-handbook</span>
        </div>
      </footer>
    </div>
  )
}
